import { useState } from "react";
import { Button } from "../../../components/buttons/button";
import { NumberCard } from "../../../components/cards/numberCard";
import Dropdown from "../../../components/dropdown/dropdown";
import { TableComponent } from "../../../components/table";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useGetSalesEarningsQuery } from "../../../redux/features/sales/sales.api";
import { currentYear } from "../../../utils/constants";
import { formatToCurrency } from "../../../utils/helpers";
import {
  EarningsSummaryData,
  QueryParams,
  SalesDashboardMetrics,
} from "../../../utils/types";

export const SalesEarning = ({
  metrics,
  earningSummary,
  fetchingSummary,
}: {
  metrics?: SalesDashboardMetrics;
  earningSummary?: EarningsSummaryData;
  fetchingSummary: boolean;
}) => {
  const [year, setYear] = useState<number>(currentYear);
  const [query, setQuery] = useState<QueryParams>({
    page: 1,
    limit: 50,
  });
  const dropDownOptions = [
    {
      label: "2025",
      value: currentYear,
      onClick: () => {
        setYear(currentYear);
      },
    },
  ];
  const { isFetching: fetchingEarnings, data: earningsData } =
    useGetSalesEarningsQuery(
      {
        ...query,
        year,
        search: query.search === "" ? undefined : query.search,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );
  return (
    <div className="mt-3 flex flex-col gap-3">
      <BgWrapper className="relative flex w-full flex-col gap-5">
        <div className="flex w-full justify-between">
          <div className="flex w-full flex-col gap-5">
            <div className="flex flex-col gap-2">
              <p className="text-base text-grey-600">Total Earnings </p>
              <p className="text-[32px] font-extrabold leading-8 text-grey-950">
                {formatToCurrency(metrics?.data?.totalCommission ?? 0, {
                  currency: "NGN",
                })}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-base text-grey-600">Total Tickets Sold</p>
              <p className="text-[32px] font-extrabold leading-8 text-grey-950">
                {metrics?.data?.totalTicketsSold}
              </p>
            </div>
          </div>
          <div className="absolute right-5 top-0 hidden w-fit md:block">
            <img src="/assets/guest.svg" alt="" />
          </div>
        </div>
        <div className="flex flex-col items-center gap-2.5 md:flex-row">
          <NumberCard
            value={formatToCurrency(earningSummary?.monthlyEarningsNGN ?? 0, {
              currency: "NGN",
            })}
            text="Earnings (This Month)"
            isCurrency={true}
            loading={fetchingSummary}
            titleFirst
          />
          <NumberCard
            value={earningSummary?.totalTicketsSold ?? 0}
            text="Tickets Sold (This Month)"
            isCurrency={false}
            loading={fetchingSummary}
            titleFirst
          />
        </div>
      </BgWrapper>
      {/* <div className="flex w-full flex-col items-start justify-between gap-2.5 rounded-xl border-[2px] border-referral-border bg-referral p-3 shadow-referral-shadow lg:flex-row lg:items-center">
        <p className="w-full text-base font-bold text-grey-900">
          Start earning with your referral link ⚡️
        </p>
        <div className="flex w-full max-w-[480px] items-center justify-between gap-2.5 rounded-xl border-[2px] border-referral-border bg-white/25 p-3">
          <div className="flex items-center gap-2.5">
            <Icon
              svg="globe"
              height={20}
              width={20}
              className="text-grey-900"
            />
            <p className="flex-shrink-0 text-base font-bold text-grey-900">
              {`app.usetikket.com/signup?ref=${metrics?.data?.referralId}`}
            </p>
          </div>
          <CopyButton
            value={`app.usetikket.com/signup?ref=${metrics?.data?.referralId}`}
            className="text-sm font-bold text-grey-950"
          />
        </div>
      </div> */}
      <BgWrapper className="flex flex-col gap-3 rounded-t-2xl">
        <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
          <Dropdown
            menuItems={dropDownOptions.map((option) => {
              return {
                name: (
                  <p className="soft-shrink flex items-center gap-2 !rounded-md !bg-white p-3 text-left !font-bold">
                    {option.label}
                  </p>
                ),
                onClick: option.onClick,
              };
            })}
            className=""
            menuClassName="!gap-1 !border-0 !bg-grey-50 !p-1 !w-[200px]"
            menuItemsClassName="border-none !text-base !bg-transparent !p-0"
            position="bottom end"
          >
            <Button
              text={year}
              icon="caret-down"
              type="button"
              iconFirst={false}
              variation="styled-grey"
              iconClassName={"mb-1"}
              className="max-w-fit"
            />
          </Dropdown>
        </div>
        <TableComponent
          headers={[]}
          rows={
            earningsData?.data?.map((earnings, idx) => {
              return {
                id: idx,
                items: [
                  <div
                    className={`flex w-full flex-1 flex-col justify-between gap-2 md:flex-row md:items-center`}
                  >
                    <div className="flex items-center gap-[2px]">
                      <p className="text-sm font-bold text-grey-900">
                        {formatToCurrency(earnings.totalEarnings?.NGN ?? 0, {
                          currency: "NGN",
                        })}
                      </p>
                      <p className="text-sm text-grey-600">
                        ∙ {earnings?.totalTicketsSold || "No"} Ticket
                        {earnings.totalTicketsSold > 1 && "s"} Sold
                      </p>
                    </div>
                    <p className="text-sm text-grey-600">
                      {earnings.month}, {currentYear}
                    </p>
                  </div>,
                ],
              };
            }) ?? []
          }
          loadingRows={query.limit}
          isLoading={fetchingEarnings}
          isRowClickable={false}
          isTableEmpty={[0, undefined].includes(earningsData?.data?.length)}
          emptyTitle="No Earning History"
          emptySubTitle="Share the referral link with event organizers to get started!"
          emptyImage="/assets/images/paper-plane-glass.svg"
          currentPage={query.page ?? 1}
          totalCount={earningsData?.data?.length ?? 0}
          itemsPerPage={query.limit!}
          pageRangeDisplayed={3}
          onPageChange={(page) => {
            setQuery({
              ...query,
              page: page,
            });
          }}
          className=""
        />
      </BgWrapper>
    </div>
  );
};
